import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface CompanyRow {
  id: string;
  name: string;
  status: string | null;
  suspension_type: string | null;
  suspension_reason: string | null;
  suspended_at: string | null;
  email: string | null;
  website: string | null;
  created_at: string;
  memberCount: number;
  adminCount: number;
  hasNoAdmin: boolean;
}

const fetchCompanies = async (): Promise<CompanyRow[]> => {
  const [companiesRes, membersRes] = await Promise.all([
    supabase
      .from("companies")
      .select(
        "id, name, status, suspension_type, suspension_reason, suspended_at, email, website, created_at"
      )
      .neq("id", "2c24dd37-7434-4b8a-919e-2a54fb7aae20")
      .order("created_at", { ascending: false }),
    supabase
      .from("company_members")
      .select("company_id, role, status")
      .eq("status", "active"),
  ]);

  if (companiesRes.error) throw new Error(companiesRes.error.message);
  if (membersRes.error) throw new Error(membersRes.error.message);

  const memberCounts = new Map<string, number>();
  const adminCounts = new Map<string, number>();
  for (const m of membersRes.data ?? []) {
    if (!m.company_id) continue;
    memberCounts.set(m.company_id, (memberCounts.get(m.company_id) ?? 0) + 1);
    if (m.role === "admin") {
      adminCounts.set(m.company_id, (adminCounts.get(m.company_id) ?? 0) + 1);
    }
  }

  return (companiesRes.data ?? []).map((c) => {
    const memberCount = memberCounts.get(c.id) ?? 0;
    const adminCount = adminCounts.get(c.id) ?? 0;
    return {
      ...c,
      memberCount,
      adminCount,
      hasNoAdmin: adminCount === 0,
    };
  });
};

export const useCompanies = () => {
  const query = useQuery({
    queryKey: ["companies"],
    queryFn: fetchCompanies,
    staleTime: 30_000,
  });

  return {
    companies: query.data ?? [],
    isLoading: query.isLoading,
    error: query.isError ? "Failed to load companies." : null,
    refetch: query.refetch,
    isFetching: query.isFetching,
  };
};
